import React, { useCallback, useEffect, useMemo, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import {
    addMontrealCalendarDays,
    montrealYmdFromDate,
} from "../../../pages/components/montrealCalendar";
import WordWheelApi from "../lib/api";
import { resolveWordWheelGridSize, WORD_WHEEL_DAILY_UNLOCK_LEVEL } from "../lib/constants";
import { useWordWheelAppearance } from "../context/WordWheelAppearanceContext";
import { resolveWordWheelView, withWordWheelView } from "../lib/viewMode";
import {
    dailyPlayPath,
    MAIN_PATH,
    WORD_WHEEL_DAILY_CALENDAR_MIN,
} from "../routes";

function parseWords(wordsInUse) {
    if (!wordsInUse) return [];
    return wordsInUse.split("\n").map((w) => w.trim()).filter(Boolean);
}

function clampYmd(ymd, minYmd, maxYmd) {
    if (ymd < minYmd) return minYmd;
    if (ymd > maxYmd) return maxYmd;
    return ymd;
}

function formatDisplayDate(ymd) {
    const [y, m, d] = ymd.split("-").map(Number);
    const date = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
    return date.toLocaleDateString(undefined, {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        timeZone: "UTC",
    });
}

function resolveJourneyLevel(puzzle) {
    if (puzzle?.mainJourneyLevel != null && Number.isFinite(Number(puzzle.mainJourneyLevel))) {
        return Number(puzzle.mainJourneyLevel);
    }
    if (puzzle?.puzzleLevel != null && Number.isFinite(Number(puzzle.puzzleLevel))) {
        return Number(puzzle.puzzleLevel);
    }
    return null;
}

export default function WordWheelDailyPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { homeColors, isRandomScene } = useWordWheelAppearance();
    const colors = homeColors;
    const view = resolveWordWheelView(searchParams.get("view"));
    const homePath = withWordWheelView(MAIN_PATH, view);
    const todayYmd = useMemo(() => montrealYmdFromDate(), []);
    const minYmd = WORD_WHEEL_DAILY_CALENDAR_MIN;

    const selectedDate = useMemo(() => {
        const fromUrl = searchParams.get("date");
        return clampYmd(fromUrl || todayYmd, minYmd, todayYmd);
    }, [searchParams, todayYmd, minYmd]);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [puzzle, setPuzzle] = useState(null);
    const [gateLoading, setGateLoading] = useState(true);
    const [journeyLevel, setJourneyLevel] = useState(null);

    const setDate = useCallback(
        (ymd) => {
            const clamped = clampYmd(ymd, minYmd, todayYmd);
            setSearchParams({ date: clamped }, { replace: true });
        },
        [minYmd, todayYmd, setSearchParams]
    );

    const shiftDay = useCallback(
        (delta) => {
            setDate(addMontrealCalendarDays(selectedDate, delta));
        },
        [selectedDate, setDate]
    );

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setGateLoading(true);
            try {
                const next = await WordWheelApi.fetchNext();
                if (cancelled) return;
                setJourneyLevel(resolveJourneyLevel(next));
            } catch {
                if (!cancelled) setJourneyLevel(null);
            } finally {
                if (!cancelled) setGateLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const dailyUnlocked =
        journeyLevel != null && journeyLevel >= WORD_WHEEL_DAILY_UNLOCK_LEVEL;

    useEffect(() => {
        if (gateLoading || !dailyUnlocked) {
            setLoading(false);
            setPuzzle(null);
            return undefined;
        }
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError("");
            setPuzzle(null);
            try {
                const data = await WordWheelApi.fetchDaily(selectedDate);
                if (cancelled) return;
                if (data?.code === "NO_DATA") {
                    setError("No puzzle available for this date.");
                    return;
                }
                setPuzzle(data);
            } catch (e) {
                if (!cancelled) setError(e?.message || "Failed to load daily puzzle");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [selectedDate, dailyUnlocked, gateLoading]);

    const words = useMemo(() => parseWords(puzzle?.wordsInUse), [puzzle]);
    const gridSize = useMemo(() => resolveWordWheelGridSize(puzzle), [puzzle]);
    const isToday = selectedDate === todayYmd;
    const canGoPrev = selectedDate > minYmd;
    const canGoNext = selectedDate < todayYmd;
    const calendarValue = useMemo(() => dayjs(selectedDate), [selectedDate]);
    const minDayjs = useMemo(() => dayjs(minYmd), [minYmd]);
    const maxDayjs = useMemo(() => dayjs(todayYmd), [todayYmd]);

    const handlePlay = useCallback(() => {
        if (puzzle?.id) {
            navigate(withWordWheelView(dailyPlayPath(selectedDate), view));
        }
    }, [navigate, puzzle, selectedDate, view]);

    const dateNavIconSx = {
        minWidth: 44,
        minHeight: 44,
        borderRadius: "50%",
        border: `1px solid ${colors.borderStrong || colors.border}`,
        bgcolor: colors.surface,
        color: colors.text,
        boxShadow: colors.shadowSm,
    };

    const sceneMuted = isRandomScene
        ? { color: "rgba(255,255,255,0.88)", textShadow: "0 1px 4px rgba(0,0,0,0.75)" }
        : null;
    const sceneText = isRandomScene
        ? { color: "#ffffff", textShadow: "0 1px 5px rgba(0,0,0,0.8)" }
        : null;

    if (gateLoading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
                <CircularProgress size={28} sx={{ color: colors.primary }} />
            </Box>
        );
    }

    if (!dailyUnlocked) {
        return (
            <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
                <IconButton
                    component={RouterLink}
                    to={homePath}
                    aria-label="Back to home"
                    sx={{ alignSelf: "flex-start", color: colors.text, ml: -1, minWidth: 44, minHeight: 44, ...sceneText }}
                >
                    <ArrowBackIcon />
                </IconButton>
                <Box
                    sx={{
                        mt: 3,
                        p: 3,
                        borderRadius: 3,
                        bgcolor: colors.surface,
                        border: `1px solid ${colors.border}`,
                        textAlign: "center",
                        boxShadow: colors.shadowMd,
                    }}
                >
                    <LockOutlinedIcon sx={{ fontSize: 40, color: colors.primaryGlow, mb: 1.5 }} />
                    <Typography sx={{ fontWeight: 800, fontSize: 20, color: colors.text, mb: 1 }}>
                        Daily Puzzle locked
                    </Typography>
                    <Typography sx={{ color: colors.textMuted, mb: 2, lineHeight: 1.45 }}>
                        Reach Season Journey level {WORD_WHEEL_DAILY_UNLOCK_LEVEL} to unlock
                        today&apos;s bonus puzzle.
                        {journeyLevel != null ? ` You are on level ${journeyLevel}.` : ""}
                    </Typography>
                    <Button
                        component={RouterLink}
                        to={homePath}
                        variant="contained"
                        sx={{
                            minHeight: 44,
                            borderRadius: 2,
                            textTransform: "none",
                            fontWeight: 700,
                            bgcolor: colors.primary,
                            "&:hover": { bgcolor: colors.accentDark || colors.primaryGlow },
                        }}
                    >
                        Continue journey
                    </Button>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
            <IconButton
                component={RouterLink}
                to={homePath}
                aria-label="Back to home"
                sx={{ alignSelf: "flex-start", color: colors.text, ml: -1, minWidth: 44, minHeight: 44, ...sceneText }}
            >
                <ArrowBackIcon />
            </IconButton>

            <Typography
                variant="overline"
                sx={{ color: colors.accent, letterSpacing: 2, display: "block", fontWeight: 600, mt: 1, ...sceneMuted }}
            >
                Bonus Puzzle
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, color: colors.text, mb: 0.5, ...sceneText }}>
                Daily Puzzle
            </Typography>
            <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2, ...sceneMuted }}>
                Pick a date to preview and play that day&apos;s puzzle. Unlocked at journey level{" "}
                {WORD_WHEEL_DAILY_UNLOCK_LEVEL}.
            </Typography>

            <Paper
                elevation={0}
                sx={{
                    p: 2,
                    mb: 2,
                    borderRadius: 3,
                    bgcolor: colors.surface,
                    border: `1px solid ${colors.border}`,
                    boxShadow: colors.shadowSm,
                }}
            >
                <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                    <IconButton
                        aria-label="Previous day"
                        onClick={() => shiftDay(-1)}
                        disabled={!canGoPrev || loading}
                        sx={dateNavIconSx}
                    >
                        <ChevronLeftIcon />
                    </IconButton>
                    <Box sx={{ textAlign: "center", flex: 1, minWidth: 0, px: 0.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: colors.text }}>
                            {formatDisplayDate(selectedDate)}
                        </Typography>
                        {isToday ? (
                            <Chip
                                label="Today"
                                size="small"
                                sx={{
                                    mt: 0.5,
                                    height: 22,
                                    bgcolor: colors.successSoft,
                                    color: colors.successText || colors.success,
                                    fontWeight: 600,
                                }}
                            />
                        ) : null}
                    </Box>
                    <IconButton
                        aria-label="Next day"
                        onClick={() => shiftDay(1)}
                        disabled={!canGoNext || loading}
                        sx={dateNavIconSx}
                    >
                        <ChevronRightIcon />
                    </IconButton>
                </Stack>
            </Paper>

            <Paper
                elevation={0}
                sx={{
                    mb: 2,
                    borderRadius: 3,
                    bgcolor: colors.surface,
                    border: `1px solid ${colors.border}`,
                    boxShadow: colors.shadowSm,
                    overflow: "hidden",
                    "& .MuiPickersCalendarHeader-label": { fontWeight: 700 },
                }}
            >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateCalendar
                        value={calendarValue}
                        onChange={(next) => {
                            if (next?.isValid()) {
                                setDate(next.format("YYYY-MM-DD"));
                            }
                        }}
                        minDate={minDayjs}
                        maxDate={maxDayjs}
                        disabled={loading}
                        sx={{ width: "100%", mx: "auto" }}
                    />
                </LocalizationProvider>
            </Paper>

            <Box
                sx={{
                    flex: 1,
                    borderRadius: 3,
                    p: 2.5,
                    mb: 2,
                    bgcolor: colors.surface,
                    border: `1px solid ${colors.border}`,
                    boxShadow: colors.shadowMd,
                    textAlign: "center",
                }}
            >
                {loading ? (
                    <CircularProgress size={28} sx={{ color: colors.accent, my: 2 }} />
                ) : puzzle ? (
                    <>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: colors.text }}>
                            {puzzle.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: colors.textSecondary, mt: 0.5 }}>
                            {words.length} words · {gridSize}×{gridSize} grid
                        </Typography>
                        {puzzle.dailyDayNumber != null ? (
                            <Typography variant="caption" sx={{ display: "block", mt: 1, color: colors.textMuted }}>
                                Daily #{puzzle.dailyDayNumber}
                            </Typography>
                        ) : null}
                    </>
                ) : (
                    <Typography variant="body2" sx={{ color: colors.textSecondary, py: 2 }}>
                        {error || "No puzzle for this date."}
                    </Typography>
                )}
            </Box>

            <Button
                variant="contained"
                size="large"
                disabled={!puzzle?.id || loading}
                onClick={handlePlay}
                sx={{
                    bgcolor: colors.accent,
                    "&:hover": { bgcolor: colors.accentDark },
                    minHeight: 44,
                    borderRadius: 2,
                    boxShadow: colors.shadowSm,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "1rem",
                }}
            >
                Play
            </Button>
        </Box>
    );
}
